import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Button,
  CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { updateProjectDocumentDeliverablesAPI } from "../stepperAPI";

const DocumentDeliverables = ({
  formData,
  updateStepData,
  classes: parentClasses,
  handleNext,
  handleBack,
  activeStep,
  stepsLength,
  markApiCallSuccessful,
  hideStepActions = false, // Hide navigation buttons when used in review page
  readOnly = false, // Read-only mode for review page
}) => {
  // State variables
  const [documents, setDocuments] = useState(() => {
    const saved = formData?.documentDeliverables || [];
    // If saved data exists and is an array of documents, use it
    if (Array.isArray(saved) && saved.length > 0) {
      return saved;
    }
    // If saved is an object with documents array, use that
    if (saved?.documents && Array.isArray(saved.documents) && saved.documents.length > 0) {
      return saved.documents;
    }
    // Default: 1 empty row
    return [
      {
        documentTypeID: "",
        fileFormatTypeID: "",
        orgResponsibleID: "",
        documentCategory: "",
        documentGroup: "",
      },
    ];
  });

  // Sync documents when formData.documentDeliverables changes (for edit mode when API data loads)
  useEffect(() => {
    if (formData?.documentDeliverables) {
      const saved = formData.documentDeliverables;
      // Handle both array format and object with documents array
      if (Array.isArray(saved) && saved.length > 0) {
        console.log("🔄 DocumentDeliverables: Syncing documents from formData (array):", saved);
        setDocuments(saved);
      } else if (saved?.documents && Array.isArray(saved.documents) && saved.documents.length > 0) {
        console.log("🔄 DocumentDeliverables: Syncing documents from formData (object.documents):", saved.documents);
        setDocuments(saved.documents);
      }
    }
  }, [formData?.documentDeliverables]);

  const [documentTypes, setDocumentTypes] = useState([]);
  const [fileFormatTypes, setFileFormatTypes] = useState([]);
  const [collaboratorTypes, setCollaboratorTypes] = useState([]);
  const [documentGroups, setDocumentGroups] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [apiError, setApiError] = useState(null);
  const dropdownAPIsLoadedRef = useRef(false);

  // API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, []);


  // Fetch all data on mount
  useEffect(() => {
    const fetchAllData = async () => {
      if (dropdownAPIsLoadedRef.current || !apiBaseUrl) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch document groups and document categories from sponsor network APIs
        const fetchDocumentGroups = async () => {
          try {
            const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDocumentGroups/false`;
            const response = await axios.get(url);
            if (response.data?.status === true || response.data?.status === "true") {
              const documentGroupsList = response.data?.data?.documentGroups || [];
              const mappedDocumentGroups = documentGroupsList
                .filter(doc => doc.activated) // Only show activated document groups
                .map((doc) => ({
                  id: doc.documentGroupID,
                  name: doc.documentGroupName,
                  type: doc.documentGroupType,
                }));
              setDocumentGroups(mappedDocumentGroups);
            }
          } catch (error) {
            console.error("Error fetching document groups:", error);
          }
        };

        const fetchDocumentCategories = async () => {
          try {
            const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDocumentCategories/false`;
            const response = await axios.get(url);
            if (response.data?.status === true || response.data?.status === "true") {
              const documentCategoryList = response.data?.documentCategoryList || response.data?.data?.documentCategoryList || [];
              const mappedDocumentCategories = documentCategoryList
                .filter(doc => doc.activated) // Only show activated document categories
                .map((doc) => ({
                  id: doc.documentCategoryID,
                  name: doc.documentCategoryName,
                  type: doc.documentCategoryType,
                }));
              setDocumentCategories(mappedDocumentCategories);
            }
          } catch (error) {
            console.error("Error fetching document categories:", error);
          }
        };

        const fetchProjectStatuses = async () => {
          try {
            const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchPDStatuses/false`;
            const response = await axios.get(url);
            if (response.data?.status === true || response.data?.status === "true") {
              const data = response.data?.data;
              const projectList = data?.projectStatuses || [];
              const formattedProjectStatuses = projectList
                .filter(status => status.activated) // Only show activated project statuses
                .map((item) => ({
                  id: item.statusID || item.status_id,
                  name: item.statusName || item.status_name || "",
                  type: item.statusType || item.status_type,
                }));
              setProjectStatuses(formattedProjectStatuses);
            }
          } catch (error) {
            console.error("Error fetching project statuses:", error);
          }
        };

        // Fetch document groups, document categories, and project statuses in parallel
        await Promise.allSettled([
          fetchDocumentGroups(),
          fetchDocumentCategories(),
          fetchProjectStatuses(),
        ]);

        // Fetch document types, file format types, and collaborator types using real APIs
        const [docTypesResult, formatTypesResult, collaboratorTypesResult] = await Promise.allSettled([
          axios.get(`${apiBaseUrl}/fetchDocumentTypes/true`),
          axios.get(`${apiBaseUrl}/fetchFileFormatTypes/true`),
          axios.get(
            `${appUrl}/admin/${
              localStorage.collabType === "CMO" ? "fetchNetworkTypes" : "fetchPartnerTypes"
            }/${localStorage.ecosystemId}`
          ),
        ]);

        if (docTypesResult.status === "fulfilled" && docTypesResult.value?.data?.data?.documentTypesList) {
          setDocumentTypes(docTypesResult.value.data.data.documentTypesList);
        }

        if (formatTypesResult.status === "fulfilled" && formatTypesResult.value?.data?.data?.fileFormatTypesList) {
          setFileFormatTypes(formatTypesResult.value.data.data.fileFormatTypesList);
        }

        if (collaboratorTypesResult.status === "fulfilled" && collaboratorTypesResult.value?.data?.data) {
          setCollaboratorTypes(collaboratorTypesResult.value.data.data);
        }

        if (
          docTypesResult.status === "rejected" ||
          formatTypesResult.status === "rejected" ||
          collaboratorTypesResult.status === "rejected"
        ) {
          setError("Some configuration data failed to load.");
        }

        dropdownAPIsLoadedRef.current = true;
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load configuration data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [apiBaseUrl]);

  // Handle document changes
  const handleDocumentChange = useCallback(
    (index, field, value) => {
      setDocuments((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        
        // When document type is selected, automatically populate category and group
        if (field === "documentTypeID" && value) {
          const selectedDocType = documentTypes.find(
            (type) => type.documentTypeID === value
          );
          
          if (selectedDocType) {
            // Set document category from the selected document type
            if (selectedDocType.documentCategoryID && selectedDocType.documentCategoryName) {
              updated[index].documentCategoryID = selectedDocType.documentCategoryID;
              updated[index].documentCategory = selectedDocType.documentCategoryName;
            } else {
              updated[index].documentCategoryID = null;
              updated[index].documentCategory = "";
            }
            
            // Set document group from the selected document type
            if (selectedDocType.documentGroupID) {
              updated[index].documentGroupID = selectedDocType.documentGroupID;
              // Find the document group name from documentGroups
              const docGroup = documentGroups.find(
                (group) => group.id === selectedDocType.documentGroupID
              );
              updated[index].documentGroup = docGroup ? docGroup.name : "";
            } else {
              updated[index].documentGroupID = null;
              updated[index].documentGroup = "";
            }
          }
        }
        
        if (typeof updateStepData === "function") {
          updateStepData("documentDeliverables", updated);
        }
        return updated;
      });
    },
    [updateStepData, documentTypes, documentGroups]
  );

  // Handle add row
  const handleAddRow = useCallback(() => {
    setDocuments((prev) => {
      const newRow = {
        documentTypeID: "",
        fileFormatTypeID: "",
        orgResponsibleID: "",
        documentCategory: "",
        documentGroup: "",
      };
      const updated = [...prev, newRow];
      
      if (typeof updateStepData === "function") {
        updateStepData("documentDeliverables", updated);
      }
      return updated;
    });
  }, [updateStepData]);

  // Handle delete row
  const handleDeleteRow = useCallback(
    (index) => {
      // Prevent deleting the first row (index 0) - it's mandatory
      if (index === 0) {
        return;
      }
      
      setDocuments((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        
        // Ensure at least one row exists
        if (updated.length === 0) {
          updated.push({
            documentTypeID: "",
            fileFormatTypeID: "",
            orgResponsibleID: "",
            documentCategory: "",
            documentGroup: "",
          });
        }
        
        if (typeof updateStepData === "function") {
          updateStepData("documentDeliverables", updated);
        }
        return updated;
      });
    },
    [updateStepData]
  );

  // Helper functions to get display names for readOnly mode
  const getDocumentTypeName = (id) => {
    if (!id) return "N/A";
    const type = documentTypes.find((t) => t.documentTypeID === id);
    return type ? type.documentTypeName : id;
  };

  const getFileFormatTypeName = (id) => {
    if (!id) return "N/A";
    const format = fileFormatTypes.find((f) => f.fileFormatTypeID === id);
    return format ? format.fileFormatTypeName : id;
  };

  const getCollaboratorTypeName = (id) => {
    if (!id) return "N/A";
    const collab = collaboratorTypes.find((c) => c.collaborator_type_id === id);
    return collab ? collab.collaborator_type_name : id;
  };

  // Helper to get cdmoOrgID from formData
  const getCdmoOrgID = useCallback(() => {
    try {
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        return parsed.productDetails?.cdmoOrgID || null;
      }
    } catch (error) {
      console.error("Error getting cdmoOrgID:", error);
    }
    return null;
  }, []);

  // Helper to get collabrixProjectID from formData
  const getCollabrixProjectID = useCallback(() => {
    try {
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        return parsed.productDetails?.collabrixProjectID || null;
      }
    } catch (error) {
      console.error("Error getting collabrixProjectID:", error);
    }
    return null;
  }, []);

  // Handle save and next
  const handleSaveAndNext = useCallback(async () => {
    setIsSaving(true);
    setApiError(null); // Clear previous errors
    
    // Validation: Check that at least one document is selected with all required fields
    const validDocuments = documents.filter((doc) => doc.documentTypeID && doc.fileFormatTypeID && doc.orgResponsibleID);
    
    if (validDocuments.length === 0) {
      setApiError("At least one document must be selected with all required fields (Document Type, File Format, and Organization Responsible)");
      setIsSaving(false);
      return;
    }
    
    try {
      // Get required IDs
      const collabrixProjectID = getCollabrixProjectID();
      const cdmoOrgID = getCdmoOrgID();
      
      // Map documents to API format
      const documentDetailsList = validDocuments.map((doc) => ({
        documentTypeID: doc.documentTypeID,
        fileFormatID: doc.fileFormatTypeID, // API uses fileFormatID
        uploadOrgCollaboratorTypeID: doc.orgResponsibleID, // API uses uploadOrgCollaboratorTypeID
        documentCategoryID: doc.documentCategoryID || null,
        documentGroupID: doc.documentGroupID || null,
      }));

      if (!collabrixProjectID || !cdmoOrgID) {
        const errorMsg = "Missing required information. Please complete the previous steps first.";
        setApiError(errorMsg);
        setIsSaving(false);
        return;
      }

      // Build payload for updateProjectDocumentDeliverables API
      const payload = {
        collabrixProjectID: collabrixProjectID,
        cdmoOrgID: cdmoOrgID,
        documentDetailsList: documentDetailsList,
      };

      console.log("=== updateProjectDocumentDeliverables API Payload ===");
      console.log(JSON.stringify(payload, null, 2));
      console.log("=== END PAYLOAD ===");

      // Call API
      const response = await updateProjectDocumentDeliverablesAPI(payload);
      
      if (response && response.status) {
        console.log("✅ updateProjectDocumentDeliverables API Response:", response);
        
        // Ensure latest data is saved to session storage
        if (typeof updateStepData === "function") {
          updateStepData("documentDeliverables", documents);
        }

        // Only navigate on success
        if (markApiCallSuccessful) {
          markApiCallSuccessful(true, null, "Document Deliverables");
        } else {
          handleNext();
        }
      } else {
        throw new Error(response?.message || "Failed to update project document deliverables");
      }
    } catch (error) {
      console.error("Error saving document deliverables:", error);
      // Show error message and do NOT navigate
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to save document deliverables. Please try again.";
      setApiError(errorMessage);
      setIsSaving(false);
    }
  }, [documents, updateStepData, markApiCallSuccessful, handleNext, getCollabrixProjectID, getCdmoOrgID]);



  return (
    <Box>
      {/* Loading indicator */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" my={3}>
          <CircularProgress />
        </Box>
      )}

      {/* Error message */}
      {error && !isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" my={3}>
          <Typography variant="body2" style={{ color: "red" }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Document Deliverables Table */}
      {!isLoading && !error && (
        <>
          <Typography 
            variant="h6" 
            className={parentClasses?.formTitle}
            style={{ marginBottom: "24px" }}
          >
            Document Deliverables
          </Typography>

          <Box style={{ marginBottom: "16px" }}>
            <Typography variant="body1" style={{ fontWeight: 500, fontSize: "1rem" }}>
              Document Files <span style={{ color: "red" }}>*</span>
            </Typography>
          </Box>

          <TableContainer 
            component={Paper}
            style={{
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <Table style={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "18%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell style={{ padding: "8px", fontSize: "0.875rem" }}>Document Type</TableCell>
                  <TableCell style={{ padding: "8px", fontSize: "0.875rem" }}>Format</TableCell>
                  <TableCell style={{ padding: "8px", fontSize: "0.875rem" }}>Org. Responsible For Document</TableCell>
                  <TableCell style={{ padding: "8px", fontSize: "0.875rem" }}>Document Category</TableCell>
                  <TableCell style={{ padding: "8px", fontSize: "0.875rem" }}>Document Group</TableCell>
                  <TableCell style={{ padding: "8px", fontSize: "0.875rem", textAlign: "center" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc, index) => (
                  <TableRow key={index}>
                    <TableCell style={{ padding: "8px" }}>
                      {readOnly ? (
                        <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
                          {getDocumentTypeName(doc.documentTypeID)}
                        </Typography>
                      ) : (
                        <Select
                          value={doc.documentTypeID || ""}
                          onChange={(e) =>
                            handleDocumentChange(index, "documentTypeID", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                          fullWidth
                          displayEmpty
                        >
                          <MenuItem value="">Select from the list</MenuItem>
                          {documentTypes
                            .filter((type) => {
                              const usedTypes = documents
                                .filter((_, i) => i !== index)
                                .map((d) => d.documentTypeID)
                                .filter((t) => t !== "");
                              return (
                                !usedTypes.includes(type.documentTypeID) ||
                                doc.documentTypeID === type.documentTypeID
                              );
                            })
                            .map((type) => (
                              <MenuItem key={type.documentTypeID} value={type.documentTypeID}>
                                {type.documentTypeName}
                              </MenuItem>
                            ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {readOnly ? (
                        <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
                          {getFileFormatTypeName(doc.fileFormatTypeID)}
                        </Typography>
                      ) : (
                        <Select
                          value={doc.fileFormatTypeID || ""}
                          onChange={(e) =>
                            handleDocumentChange(index, "fileFormatTypeID", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                          fullWidth
                          displayEmpty
                        >
                          <MenuItem value="">Select from the list</MenuItem>
                          {fileFormatTypes.map((format) => (
                            <MenuItem key={format.fileFormatTypeID} value={format.fileFormatTypeID}>
                              {format.fileFormatTypeName}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {readOnly ? (
                        <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
                          {getCollaboratorTypeName(doc.orgResponsibleID)}
                        </Typography>
                      ) : (
                        <Select
                          value={doc.orgResponsibleID || ""}
                          onChange={(e) =>
                            handleDocumentChange(index, "orgResponsibleID", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                          fullWidth
                          displayEmpty
                        >
                          <MenuItem value="">Select from the list</MenuItem>
                          {collaboratorTypes.map((collab) => (
                            <MenuItem key={collab.collaborator_type_id} value={collab.collaborator_type_id}>
                              {collab.collaborator_type_name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      <Typography variant="body2" style={{ fontSize: "0.875rem" }}>
                        {doc.documentCategory || doc.documentCategoryName || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      <Typography variant="body2" style={{ fontSize: "0.875rem" }}>
                        {doc.documentGroup || doc.documentGroupName || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ padding: "8px", textAlign: "center" }}>
                      {!readOnly && index > 0 && (
                        <IconButton
                          onClick={() => handleDeleteRow(index)}
                          size="small"
                          style={{
                            color: "#d32f2f",
                            padding: "4px",
                          }}
                          title="Delete row"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add Row Button - Hidden in read-only mode */}
          {!readOnly && (
            <Box style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddRow}
                style={{
                  textTransform: "none",
                  borderColor: "#000",
                  color: "#000",
                }}
              >
                Add Row
              </Button>
            </Box>
          )}
        </>
      )}

      {/* API Error Message */}
      {apiError && (
        <Box
          style={{
            marginTop: "24px",
            padding: "12px 16px",
            backgroundColor: "#ffebee",
            border: "1px solid #f44336",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          <Typography style={{ color: "#d32f2f", fontSize: "14px", fontWeight: 500 }}>
            {apiError}
          </Typography>
        </Box>
      )}

      {/* Navigation Buttons - Hidden when used in review page */}
      {!hideStepActions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "80px",
            paddingTop: "24px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            variant="contained"
            onClick={handleBack}
            className={parentClasses?.button}
            style={{
              backgroundColor: "#9E9E9E",
              color: "#fff",
              padding: "10px 24px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              borderRadius: 30,
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAndNext}
            disabled={isSaving}
            className={parentClasses?.button}
            style={{
              backgroundColor: "#ec6535",
              color: "#fff",
              padding: "10px 32px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 2px 4px rgba(236, 101, 53, 0.3)",
              borderRadius: 30,
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: "#fff" }} />
                Saving...
              </>
            ) : (
              "Save Project & Add Campaign"
            )}
          </Button>
        </Box>
      )}

    </Box>
  );
};

export default DocumentDeliverables;
